import { type ReactNode } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';

import {
  categoryDisplayLabel,
  categoryImpactHint,
  COST_QUICK_SUGGESTIONS,
  inferCostCategory,
  type FinanceCostCategory,
  type FinanceCostFrequency,
} from './costRuleInference';

export type FinanceCostRuleForm = {
  id: string;
  label: string;
  category: FinanceCostCategory;
  amount: string;
  frequency: FinanceCostFrequency;
  categoryManual: boolean;
};

export type FinanceCostSettingsForm = {
  rules: FinanceCostRuleForm[];
};

export const emptyFinanceCostSettingsForm = (): FinanceCostSettingsForm => ({
  rules: [],
});

const CONTROL_HEIGHT = 40;
const textSecondary = palette.text.secondary;

const controlSx = {
  width: '100%',
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  borderRadius: 2,
  backgroundColor: palette.common.white,
  '& .MuiOutlinedInput-root': {
    height: CONTROL_HEIGHT,
    minHeight: CONTROL_HEIGHT,
    borderRadius: 2,
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.22)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    minHeight: `${CONTROL_HEIGHT - 2}px !important`,
    py: 0,
  },
  '& .MuiSelect-icon': {
    color: textSecondary,
  },
  '& .MuiInputBase-input': {
    py: 0,
    height: '100%',
    boxSizing: 'border-box',
  },
} as const;

const columnLabelSx = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: textSecondary,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  lineHeight: 1.3,
} as const;

const fieldStackGap = 1.25;

const selectMenuPaperSx = {
  '& .MuiMenuItem-root': {
    fontSize: '0.8125rem',
    fontWeight: 500,
    minHeight: 36,
  },
} as const;

const ruleGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'minmax(0, 2fr) minmax(130px, 1fr) minmax(120px, 0.85fr) minmax(112px, 0.85fr) 40px',
  },
  columnGap: 1,
  rowGap: { xs: 1, md: 0 },
  alignItems: 'start',
} as const;

const actionButtonSx = {
  minHeight: CONTROL_HEIGHT,
  height: CONTROL_HEIGHT,
  px: 2,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  borderRadius: 2,
} as const;

function newRuleForm(partial?: Partial<FinanceCostRuleForm>): FinanceCostRuleForm {
  const label = partial?.label ?? '';
  const frequency = partial?.frequency ?? 'monthly';
  const category =
    partial?.category ??
    inferCostCategory(label, frequency);
  return {
    id: partial?.id ?? crypto.randomUUID(),
    label,
    category,
    amount: partial?.amount ?? '',
    frequency,
    categoryManual: partial?.categoryManual ?? false,
  };
}

export function financeCostSettingsToForm(settings?: {
  rules?: Array<{
    id: string;
    label: string;
    category: string;
    amount: number;
    frequency: string;
  }>;
} | null): FinanceCostSettingsForm {
  return {
    rules: (settings?.rules ?? []).map((rule) =>
      newRuleForm({
        id: rule.id,
        label: rule.label,
        category: rule.category as FinanceCostCategory,
        amount: String(rule.amount),
        frequency: rule.frequency as FinanceCostFrequency,
        categoryManual: true,
      }),
    ),
  };
}

export function parseFinanceCostSettingsForm(form: FinanceCostSettingsForm): {
  rules: Array<{
    id: string;
    label: string;
    category: FinanceCostCategory;
    amount: number;
    frequency: FinanceCostFrequency;
  }>;
} {
  return {
    rules: form.rules
      .map((rule) => {
        const label = rule.label.trim();
        const amount = Number(rule.amount.replace(/\s/g, '').replace(',', '.'));
        if (!label || !Number.isFinite(amount) || amount <= 0) return null;
        return {
          id: rule.id,
          label,
          category: rule.category,
          amount,
          frequency: rule.frequency,
        };
      })
      .filter((rule): rule is NonNullable<typeof rule> => rule !== null),
  };
}

interface CustomCostsPanelProps {
  open: boolean;
  onToggle: () => void;
  form: FinanceCostSettingsForm;
  onChange: (next: FinanceCostSettingsForm) => void;
  onSave: () => void;
  onClear: () => void;
  saving: boolean;
  dirty: boolean;
  savedSummary?: string | null;
}

function updateRule(
  rules: FinanceCostRuleForm[],
  id: string,
  patch: Partial<FinanceCostRuleForm>,
): FinanceCostRuleForm[] {
  return rules.map((rule) => {
    if (rule.id !== id) return rule;
    const next = { ...rule, ...patch };
    if (patch.label !== undefined && !next.categoryManual) {
      next.category = inferCostCategory(next.label, next.frequency);
    }
    if (patch.frequency !== undefined && !next.categoryManual) {
      next.category = inferCostCategory(next.label, next.frequency);
    }
    return next;
  });
}

function RuleField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: fieldStackGap }}>
      <Typography component="label" sx={columnLabelSx}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export function CustomCostsPanel({
  open,
  onToggle,
  form,
  onChange,
  onSave,
  onClear,
  saving,
  dirty,
  savedSummary,
}: CustomCostsPanelProps) {
  const addRule = (partial?: Partial<FinanceCostRuleForm>) => {
    onChange({
      rules: [...form.rules, newRuleForm(partial)],
    });
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        borderTop: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        pt: 1.5,
      }}
    >
      <Button
        onClick={onToggle}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 180ms ease',
            }}
          />
        }
        sx={{
          px: 0,
          minHeight: 32,
          textTransform: 'none',
          fontWeight: 700,
          color: palette.text.primary,
          justifyContent: 'flex-start',
        }}
      >
        Additional costs
        {form.rules.length > 0 && (
          <Chip
            label={form.rules.length}
            size="small"
            sx={{ ml: 1, height: 20, fontSize: 11, fontWeight: 700 }}
          />
        )}
      </Button>

      <Collapse in={open}>
        <Box sx={{ pt: 1.25 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, maxWidth: 720, lineHeight: 1.55 }}
          >
            Costs not tracked in connectors. We classify them automatically and update CM rows in
            the table below.
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.75 }}>
            {COST_QUICK_SUGGESTIONS.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                size="small"
                onClick={() => addRule({ label: suggestion })}
                icon={<AddRoundedIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  height: 30,
                  pl: 0.25,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '999px',
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  bgcolor: palette.common.white,
                  color: palette.text.primary,
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: palette.primary.main, ml: 0.75 },
                  '&:hover': {
                    bgcolor: `${palette.primary.main}08`,
                    borderColor: `${palette.primary.main}40`,
                  },
                }}
              />
            ))}
          </Stack>

          {form.rules.length === 0 ? (
            <Box
              sx={{
                py: 3,
                px: 2,
                borderRadius: 2,
                border: '1px dashed rgba(15, 23, 42, 0.16)',
                bgcolor: 'rgba(248, 250, 252, 0.8)',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                No additional costs yet.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={() => addRule()}
                sx={actionButtonSx}
              >
                Add cost
              </Button>
            </Box>
          ) : (
            <Stack spacing={1}>
              {form.rules.map((rule) => (
                <Box
                  key={rule.id}
                  sx={{
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: 2,
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    bgcolor: '#fafbfc',
                  }}
                >
                  <Box sx={ruleGridSx}>
                    <RuleField label="Name">
                      <TextField
                        placeholder="TikTok, company costs…"
                        size="small"
                        hiddenLabel
                        value={rule.label}
                        onChange={(e) =>
                          onChange({
                            rules: updateRule(form.rules, rule.id, { label: e.target.value }),
                          })
                        }
                        sx={controlSx}
                      />
                    </RuleField>

                    <RuleField label="Type">
                      <FormControl size="small" sx={controlSx}>
                        <Select
                          value={rule.category}
                          onChange={(e) =>
                            onChange({
                              rules: updateRule(form.rules, rule.id, {
                                category: e.target.value as FinanceCostCategory,
                                categoryManual: true,
                              }),
                            })
                          }
                          renderValue={(value) =>
                            categoryDisplayLabel(value as FinanceCostCategory)
                          }
                          MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                        >
                          <MenuItem value="marketing">Marketing</MenuItem>
                          <MenuItem value="operating">Operating</MenuItem>
                          <MenuItem value="per_order">Per order</MenuItem>
                        </Select>
                      </FormControl>
                    </RuleField>

                    <RuleField label="Frequency">
                      <FormControl size="small" sx={controlSx}>
                        <Select
                          value={rule.frequency}
                          onChange={(e) =>
                            onChange({
                              rules: updateRule(form.rules, rule.id, {
                                frequency: e.target.value as FinanceCostFrequency,
                              }),
                            })
                          }
                          MenuProps={{ PaperProps: { sx: selectMenuPaperSx } }}
                        >
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="per_order">Per order</MenuItem>
                        </Select>
                      </FormControl>
                    </RuleField>

                    <RuleField label="Amount">
                      <TextField
                        placeholder="0"
                        size="small"
                        hiddenLabel
                        type="number"
                        value={rule.amount}
                        onChange={(e) =>
                          onChange({
                            rules: updateRule(form.rules, rule.id, { amount: e.target.value }),
                          })
                        }
                        InputProps={{
                          startAdornment: (
                            <Typography
                              component="span"
                              sx={{
                                mr: 0.5,
                                color: textSecondary,
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                              }}
                            >
                              €
                            </Typography>
                          ),
                        }}
                        sx={controlSx}
                      />
                    </RuleField>

                    <Box
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        gap: fieldStackGap,
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        component="span"
                        aria-hidden
                        sx={{ ...columnLabelSx, visibility: 'hidden', userSelect: 'none' }}
                      >
                        Remove
                      </Typography>
                      <IconButton
                        aria-label="Remove cost"
                        onClick={() =>
                          onChange({ rules: form.rules.filter((item) => item.id !== rule.id) })
                        }
                        sx={{
                          width: CONTROL_HEIGHT,
                          height: CONTROL_HEIGHT,
                          borderRadius: 2,
                          border: '1px solid rgba(15, 23, 42, 0.1)',
                          bgcolor: palette.common.white,
                          color: textSecondary,
                          '&:hover': {
                            bgcolor: 'rgba(239, 68, 68, 0.06)',
                            borderColor: 'rgba(239, 68, 68, 0.25)',
                            color: '#dc2626',
                          },
                        }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1, gap: 1 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4, fontSize: '0.6875rem', flex: 1, minWidth: 0 }}
                    >
                      {categoryImpactHint(rule.category)}
                    </Typography>
                    <IconButton
                      aria-label="Remove cost"
                      onClick={() =>
                        onChange({ rules: form.rules.filter((item) => item.id !== rule.id) })
                      }
                      sx={{
                        display: { xs: 'inline-flex', md: 'none' },
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        border: '1px solid rgba(15, 23, 42, 0.1)',
                        bgcolor: palette.common.white,
                        color: textSecondary,
                        flexShrink: 0,
                        '&:hover': {
                          bgcolor: 'rgba(239, 68, 68, 0.06)',
                          borderColor: 'rgba(239, 68, 68, 0.25)',
                          color: '#dc2626',
                        },
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {form.rules.length > 0 && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ mt: 1.75 }}
            >
              <Button
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={() => addRule()}
                sx={actionButtonSx}
              >
                Add cost
              </Button>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  startIcon={<SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                  disabled={saving || !dirty}
                  onClick={onSave}
                  sx={actionButtonSx}
                >
                  Save costs
                </Button>
                <Button
                  variant="text"
                  disabled={saving || form.rules.length === 0}
                  onClick={onClear}
                  sx={{
                    ...actionButtonSx,
                    color: textSecondary,
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                  }}
                >
                  Clear all
                </Button>
              </Stack>
            </Stack>
          )}

          {savedSummary && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1.25,
                fontWeight: 600,
                color: palette.primary.main,
                lineHeight: 1.5,
              }}
            >
              Saved: {savedSummary}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default CustomCostsPanel;
